import { useCallback, useEffect, useRef, useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import { useMe } from '../lib/use-me';
import { initialsFrom } from '../lib/initials';
import '../styles/user-menu.scss';

export function UserMenu() {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // A menu that only closes by clicking its own trigger is a trap on touch
  // devices, where there is no Escape key.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSignOut = useCallback(() => {
    void signOut();
  }, []);

  // Fall back to the email if the org row has not loaded, so the avatar is
  // never a blank circle.
  const label = me?.username ?? me?.email ?? '';

  return (
    <div className="x5-user" ref={rootRef}>
      <button
        type="button"
        className="x5-user__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="x5-user__avatar" aria-hidden="true">
          {label ? initialsFrom(label) : ''}
        </span>
      </button>

      {open && (
        <div className="x5-user__panel" role="menu">
          <div className="x5-user__identity">
            <span className="x5-user__avatar x5-user__avatar--lg" aria-hidden="true">
              {label ? initialsFrom(label) : ''}
            </span>
            <div className="x5-user__who">
              <p className="x5-user__email" title={me?.email ?? label}>
                {me?.email ?? label}
              </p>
              {me?.email && me.username !== me.email && (
                <p className="x5-user__username">{me.username}</p>
              )}
            </div>
          </div>

          {me?.organization && (
            <div className="x5-user__row">
              <span className="x5-user__row-label">Organization</span>
              <span className="x5-user__row-value">{me.organization.name}</span>
            </div>
          )}

          <button type="button" className="x5-user__action" role="menuitem" onClick={handleSignOut}>
            <span>Sign out</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M10 8l-4 4 4 4M6 12h9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
